/* globals jQuery */

import delegateEventListener from "./helpers/delegate-event";
import determineTarget from "./helpers/determine-target";
import toggleElement from "./helpers/toggle-element";
import setupModals from "./helpers/modals.js";
import Logger from "./helpers/logging.js";

jQuery($ => {
  // =========================
  // ! Analytics/Logging
  // =========================

  const logger = new Logger(
    window.loggingEndpoint ?? "",
    window.projectName ?? "Untitled"
  );

  delegateEventListener("click", "a", (event, trigger) => {
    const targetHref = trigger.getAttribute("href");
    if (targetHref.startsWith("#")) {
      logger.event({
        event_type: "internal_link",
        target: targetHref,
        location: trigger.closest("[data-module]").dataset.module ?? null,
      });
    } else {
      logger.event({
        event_type: "external_link",
        target: trigger.getAttribute("href"),
        location: trigger.closest("[data-module]")?.dataset.module ?? null,
      });
    }
  });

  delegateEventListener("click", "[data-action]", (event, trigger) => {
    logger.event({
      event_type: "internal_link",
      target: `${trigger.dataset.action}:${
        trigger.getAttribute("aria-controls") ??
        trigger.closest("[id]").id ??
        "unknown"
      }`,
      location: trigger.closest("[data-module]").dataset.module ?? null,
    });
  });

  // =========================
  // ! Stats Pulling
  // =========================

  // Initialize all results to start
  document.querySelectorAll( '[data-stat]' ).forEach( entry => {
    entry.dataset.result = 0;
  } );

  if (typeof window.statsEndpoint === 'string') {
    fetch(window.statsEndpoint)
    .then(result => result.text())
    .then(htmlString => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const [ headerRow, ...rows ] = doc.querySelectorAll( 'table tbody tr' );
      const headers = headerRow.querySelectorAll( 'td' );

      const stats = new Map();
      for ( const row of rows ) {
        const item = new Map();
        const cells = row.querySelectorAll( 'td' );
        for ( let i = 0; i < cells.length; i++ ) {
          item.set( headers[ i ].innerText, cells[ i ].innerText );
        }

        const question = item.get( 'question_name' );
        const answer = item.get( 'answer_text' );
        const count = parseInt( item.get( 'adjCnt' ) || 0 );

        if ( question && answer ) {
          const stat = stats.get( question ) ?? [];
          stat.push( [ answer, count ] );
          stats.set( question, stat );
        }
      }

      for ( const [ question, counts ] of stats.entries() ) {
        const total = counts.reduce( ( total, count ) => total += count[1], 0 );
        for ( const count of counts ) {
          const key = `${ question }:${ count[0] }`;
          const entry = document.querySelector( `[data-stat="${ key }"]` );
          if ( entry ) {
            entry.dataset.result = Math.round( ( count[1] / total ) * 100 ) + '%';
          }
        }
      }
    } );
  }

  // =========================
  // ! Basic Toggles
  // =========================

  delegateEventListener("click", ".js-toggle", (event, trigger) => {
    const target = determineTarget(trigger);
    if (target) {
      event.preventDefault();
      const isOpen = toggleElement(target, null, "focus");

      const location = trigger.closest("[data-module]")
        ? trigger.closest("[data-module]").dataset.module
        : window.location.hash;
      logger.event({
        event_type: isOpen ? "modal-open" : "modal-close",
        target: target.id,
        location: location,
      });

      // For global styling that might be needed
      document.body.classList.toggle(`show-${target.id}`, isOpen);
    }
  });

  delegateEventListener("click", ".js-close", (event, trigger) => {
    event.preventDefault();
    toggleElement(trigger.closest('[aria-hidden="false"]'), false);
  });

  // =========================
  // ! Mobile Nav Handling
  // =========================

  $(".menu a").click(function () {
    toggleElement("navbar");
    document.body.classList.remove("show-navbar");
  });

  // =========================
  // ! Modals
  // =========================

  // Add classes to modals automatically
  $(".modal").addClass("js-modal");
  $(".modal__close").addClass("js-modal-close");
  $(".modal__frame").addClass("js-modal-backdrop");

  setupModals();

  // =========================
  // ! Minimodals
  // =========================

  const inViewObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
          entry.target.classList.add("currently-in-view");
          entry.target
            .querySelector(".minimodal")
            ?.setAttribute("aria-hidden", false);
        } else {
          entry.target.classList.remove("currently-in-view");
        }
      });
    },
    {
      rootMargin: "0% -30% 0% -30%",
    }
  );

  document
    .querySelectorAll(".js-inviewable")
    .forEach(section => inViewObserver.observe(section));

  // =========================
  // ! Form Stuff
  // =========================

  function logSlideAnswers(slide, skip) {
    const module =
      slide.closest("[data-module]").dataset.module ?? "Unknown Module";
    const inputs = slide.querySelectorAll("input");
    // Assuming one input control/group per slide
    logger.answer({
      module,
      question_type: inputs[0].type,
      question_name: inputs[0].name,
      answer_text: skip
        ? "skipped"
        : [...inputs]
            .filter(input => {
              if (input.type === "radio" || input.type === "checkbox") {
                return input.checked;
              }

              return input.validity.valid;
            })
            .map(input => input.value)
            .join(","),
    });
  }

  document.querySelectorAll('input[type="range"]').forEach(input => {
    function updateValue() {
      input.style.setProperty("--value", input.value || 50);
    }

    input.addEventListener("input", updateValue);
    input.form.addEventListener("reset", () => setTimeout(updateValue));
    updateValue(input.value || 50);
  });

  document.querySelectorAll(".js-survey-carousel").forEach(survey => {
    const slides = survey.querySelectorAll(".survey-slide");
    const wrapper = survey.querySelector(".survey__wrapper");
    const track = survey.querySelector(".survey__slides");
    const finish = survey.querySelector(".survey__finish");
    const restart = survey.querySelector(".js-survey-restart");

    track.scrollLeft = 0;

    slides.forEach((slide, index) => {
      const submit = slide.querySelector(".js-survey-submit");
      const skip = slide.querySelector(".js-survey-skip");
      const back = slide.querySelector(".js-survey-back");
      const next = slide.querySelector(".js-survey-next");

      submit.disabled = true;
      back.hidden = index === 0;
      next.disabled = true;
      next.hidden = index === slides.length - 1;

      submit.addEventListener("click", () => {
        logSlideAnswers(slide);
        if (index < slides.length - 1) {
          track.scrollLeft = slides[index + 1].offsetLeft;
          next.disabled = false;
        } else {
          wrapper.hidden = true;
          finish.hidden = false;
        }
      });

      skip.addEventListener("click", () => {
        logSlideAnswers(slide, true);
        if (index < slides.length - 1) {
          track.scrollLeft = slides[index + 1].offsetLeft;
          next.disabled = false;
        } else {
          wrapper.hidden = true;
          finish.hidden = false;
        }
      });

      back.addEventListener("click", () => {
        if (index > 0) {
          track.scrollLeft = slides[index - 1].offsetLeft;
        }
      });

      next.addEventListener("click", () => {
        if (index < slides.length - 1) {
          track.scrollLeft = slides[index + 1].offsetLeft;
        }
      });

      slide.addEventListener("input", () => {
        submit.disabled =
          slide.querySelectorAll(
            'input:checked, input:not([type="checkbox"],[type="radio"]):valid'
          ).length === 0;
      });
    });

    restart.addEventListener("click", () => {
      logger.event({
        event_type: "click",
        target: "reset-survey",
        location: survey.closest("[data-module]").dataset.module ?? null,
      });

      wrapper.hidden = false;
      finish.hidden = true;
      track.scrollLeft = 0;

      survey.reset();
      slides[0].querySelector(".js-survey-back").hidden = true;
      slides[0].querySelector(".js-survey-next").hidden = false;
    });
  });

  document.querySelectorAll(".js-survey-wizard").forEach(survey => {
    const validSelector =
      'input:checked, input:not([type="checkbox"],[type="radio"]):valid';

    const inner = survey.querySelector(".survey__inner");
    const finish = survey.querySelector(".survey__finish");
    const restart = survey.querySelector(".js-survey-restart");

    const questions = survey.querySelectorAll(".survey-question");
    const total = questions.length;
    let current = 0;

    const submit = survey.querySelector(".js-survey-submit");
    const skip = survey.querySelector(".js-survey-skip");
    const back = survey.querySelector(".js-survey-back");
    const next = survey.querySelector(".js-survey-next");

    const currentText = survey.querySelector(".js-survey-current");
    const totalText = survey.querySelector(".js-survey-total");

    if (totalText) {
      totalText.innerText = total;
    }

    function goToSlide(index) {
      if (back) {
        back.disabled = index === 0;
      }
      if (next) {
        next.disabled = !questions[current].querySelector(validSelector);
      }
      if (currentText) {
        currentText.innerText = index + 1;
      }

      const nextClass = index > current ? "in" : "out";
      const prevClass = index > current ? "out" : "in";

      questions[current].hidden = true;
      questions[current].classList.remove(nextClass);
      questions[current].classList.add(prevClass);
      current = index;
      questions[current].hidden = false;
      questions[current].classList.remove(prevClass);
      questions[current].classList.add(nextClass);

      if (submit) {
        const requiredCount = parseInt( questions[current].dataset.requiredCount ?? '1' );

        submit.disabled =
          questions[current].querySelectorAll(validSelector).length < requiredCount;
      }
    }

    goToSlide(0);

    back?.addEventListener("click", () => {
      if (current > 0) {
        goToSlide(current - 1);
      }
    });

    function handleNext(skip) {
      logSlideAnswers(questions[current], skip === true);
      if (current < total - 1) {
        goToSlide(current + 1);
      } else {
        inner.hidden = true;
        finish.hidden = false;
      }
    }

    next?.addEventListener("click", handleNext);

    if (survey.classList.contains("autosubmit")) {
      survey.addEventListener("input", () => {
        questions[current].classList.add("answered");
        setTimeout(() => {
          handleNext();
          questions[current].classList.remove("answered");
        }, 1000);
      });
    } else {
      survey.addEventListener("input", event => {
        if (event.target.classList.contains("js-survey-restart")) {
          return;
        }

        const requiredCount = parseInt( questions[current].dataset.requiredCount ?? '1' );

        submit.disabled =
          questions[current].querySelectorAll(validSelector).length < requiredCount;
      });
    }

    submit?.addEventListener("click", handleNext);
    skip?.addEventListener("click", () => handleNext(true));

    restart?.addEventListener("click", () => {
      logger.event({
        event_type: "click",
        target: "reset-survey",
        location: survey.closest("[data-module]").dataset.module ?? null,
      });

      if (back) {
        back.disabled = true;
      }
      if (next) {
        next.disabled = false;
      }

      inner.hidden = false;
      finish.hidden = true;

      survey.reset();
      questions.forEach(question => {
        question.classList.remove("in");
        question.classList.remove("out");
        question.classList.remove("answered");
      });

      goToSlide(0);
    });
  });

  // =========================
  // ! Eye Tracking
  // =========================

  let eyeCount = 0;
  document.querySelectorAll(".eyes").forEach(placeholder => {
    const shape = placeholder.dataset.shape ?? "rect";
    const width = parseInt(placeholder.dataset.width ?? 100);
    const height = parseInt(placeholder.dataset.height ?? width);
    const pupilSize = parseInt(
      placeholder.dataset.pupil ?? Math.max(width, height) / 4
    );
    const corner = parseInt(placeholder.dataset.corner ?? 0);
    const gap = parseInt(placeholder.dataset.gap ?? 20);
    const path = placeholder.dataset.path ?? "";

    const canvas = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    canvas.dataset.width = width;
    canvas.dataset.height = height;
    canvas.setAttribute("class", "eyes-canvas");
    canvas.setAttribute("width", width * 2 + gap);
    canvas.setAttribute("height", height);
    canvas.setAttribute("viewBox", `0 0 ${width * 2 + gap} ${height}`);
    canvas.setAttribute("style", placeholder.getAttribute("style") ?? "");

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

    const clipPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "clipPath"
    );
    clipPath.id = `eye${++eyeCount}`;

    defs.appendChild(clipPath);
    canvas.appendChild(defs);

    for (let i = 0; i < 2; i++) {
      const eyeWrapper = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g"
      );
      eyeWrapper.setAttribute("class", "eye");
      eyeWrapper.setAttribute("clip-path", `url(#${clipPath.id})`);
      eyeWrapper.setAttribute("transform", `translate(${(width + gap) * i})`);

      const eyeShape = document.createElementNS(
        "http://www.w3.org/2000/svg",
        shape
      );
      eyeShape.setAttribute("class", "eye-shape");
      switch (shape) {
        case "rect":
          eyeShape.setAttribute("x", 0);
          eyeShape.setAttribute("y", 0);
          eyeShape.setAttribute("width", width);
          eyeShape.setAttribute("height", height);
          eyeShape.setAttribute("rx", corner);
          eyeShape.setAttribute("ry", corner);
          break;

        case "circle":
          eyeShape.setAttribute("cx", width / 2);
          eyeShape.setAttribute("cy", height / 2);
          eyeShape.setAttribute("r", Math.min(width, height) / 2);
          break;

        case "ellipse":
          eyeShape.setAttribute("cx", width / 2);
          eyeShape.setAttribute("cy", height / 2);
          eyeShape.setAttribute("rx", width / 2);
          eyeShape.setAttribute("ry", height / 2);
          break;

        case "path":
          eyeShape.setAttribute("d", path);
          break;
      }

      const eyePupil = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "circle"
      );
      eyePupil.setAttribute("class", "eye-pupil");
      eyePupil.setAttribute("cx", width / 2);
      eyePupil.setAttribute("cy", height / 2);
      eyePupil.setAttribute("r", pupilSize);

      if (i === 0) {
        const clipShape = eyeShape.cloneNode();
        clipShape.setAttribute("fill", "white");
        clipPath.append(clipShape);
      }

      eyeWrapper.appendChild(eyeShape);
      eyeWrapper.appendChild(eyePupil);
      canvas.appendChild(eyeWrapper);
    }

    placeholder.after(canvas);

    if (!placeholder.classList.contains("fallback")) {
      placeholder.remove();
    }
  });

  //https://codepen.io/nikoladragan/pen/vdpYOr

  const eyes = document.querySelectorAll(".eye");
  const pupils = document.querySelectorAll(".eye-pupil");

  let lastPointerPosition = JSON.parse(
    localStorage.getItem("lastPointerPosition")
  ) || { x: 0, y: 0 };

  // Function to update and store position
  function updatePointerPosition(x, y) {
    lastPointerPosition = { x, y };
    localStorage.setItem(
      "lastPointerPosition",
      JSON.stringify(lastPointerPosition)
    );
  }

  function eyesPosition(x, y) {
    for (let i = 0; i < eyes.length; i++) {
      const el = eyes[i].getBoundingClientRect();
      const width = parseInt(eyes[i].parentElement.dataset.width ?? 100);
      const height = parseInt(eyes[i].parentElement.dataset.height ?? 100);

      const eyeCenterX = el.left + el.width / 2;
      const eyeCenterY = el.top + el.height / 2;

      const dX = Math.max(Math.min(eyeCenterX - x, width * 2), width * -2);
      const dY = Math.max(Math.min(eyeCenterY - y, height * 2), height * -2);

      const pX = (dX * width) / (width * 2);
      const movePupilX = (((width / 3) * pX) / width) * -1;

      const pY = (dY * height) / (height * 2);
      const movePupilY = (((height / 3) * pY) / height) * -1;

      pupils[i].style.transform = `translate(${movePupilX}px, ${movePupilY}px)`;
    }
  }

  function doRefreshEyesPosition() {
    eyesPosition(lastPointerPosition.x, lastPointerPosition.y);
  }

  let refreshTimeout;
  function refreshEyesPosition() {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(doRefreshEyesPosition, 100);
  }

  doRefreshEyesPosition();

  // Touch movement
  ["touchmove", "touchstart"].forEach(eventType => {
    document.addEventListener(eventType, e => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        updatePointerPosition(touch.clientX, touch.clientY);
      }
    });
  });

  // Mousedown
  document.addEventListener("mousedown", e => {
    updatePointerPosition(e.clientX, e.clientY);
  });

  // Mouse movement
  document.addEventListener("mousemove", function (e) {
    const mX = e.clientX;
    const mY = e.clientY;

    eyesPosition(mX, mY);

    updatePointerPosition(mX, mY);
  });

  // =========================
  // ! Horizontal Page Scroll
  // =========================

  const pageContent = document.querySelector(".page-content");

  const header = document.querySelector(".header");
  const sections = [...document.querySelectorAll(".section")];
  const mainSections = [...document.querySelectorAll(".section--main")];
  mainSections.forEach((section, index) => (section.dataset.index = index));

  // Progress Bar
  const progressTitle = document.querySelector(".progress-title");
  const progressBar = document.querySelector(".progress-bar");
  const titles = [];

  const progressBarTrack = document.createElement("div");
  progressBarTrack.classList.add("progress-bar__track");
  progressBar.appendChild(progressBarTrack);
  const progressBarFill = document.createElement("div");
  progressBarFill.classList.add("progress-bar__fill");
  progressBarTrack.appendChild(progressBarFill);
  const progressBarLabel = document.createElement("div");
  progressBarLabel.classList.add("progress-bar__label");
  progressBarLabel.textContent = "0%";
  progressBar.appendChild(progressBarLabel);

  // Create segments
  let totalWidth = 0;
  mainSections.forEach(section => {
    const badge = section.querySelector(".badge");
    if (badge) {
      const title = document.createElement("span");
      title.innerHTML = `<strong>${
        section.querySelector(".badge").innerHTML
      }</strong>: ${section.querySelector("h2").innerText}`;
      title.hidden = true;
      titles.push(title);
      progressTitle.append(title);
    }

    const segment = document.createElement("a");
    segment.href = "#" + section.id;
    segment.classList.add("progress-bar__segment");

    let width = section.offsetWidth;
    let target = section;
    while (target.nextElementSibling && !target.nextElementSibling.id) {
      width += target.nextElementSibling.offsetWidth;
      target = target.nextElementSibling;
    }
    totalWidth += width;
    segment.style.setProperty("--section-width", width);

    progressBarTrack.appendChild(segment);
  });

  progressBarTrack.style.setProperty("--total-width", totalWidth);

  const breakpoint = window.matchMedia(
    `(min-width: ${1000 / 16}em) and (min-height: ${600 / 16}em)`
  );

  let lastSection;
  pageContent.addEventListener("scroll", function () {
    console.log("scroll");
    header.classList.toggle(
      "progressbar-in-view",
      pageContent.scrollLeft > pageContent.offsetWidth
    );

    refreshEyesPosition();
    const trackWith =
      (progressBarTrack.offsetWidth - progressBarLabel.offsetWidth) / 100;
    const progress =
      (Math.max(0, pageContent.scrollLeft - pageContent.offsetWidth) /
        (pageContent.scrollWidth - pageContent.offsetWidth * 2)) *
      100;
    progressBarFill.style.width = `${progress}%`;
    progressBarLabel.style.left = `${progress * trackWith}px`;
    progressBarLabel.textContent = `${Math.round(progress)}%`;

    const currentSection = mainSections.findLastIndex(
      section =>
        section.offsetLeft <
        pageContent.scrollLeft + Math.floor(pageContent.offsetWidth / 2)
    );

    if (lastSection !== currentSection) {
      if (lastSection !== undefined) {
        logger.event({
          event_type: "scroll",
          target: `title-${currentSection}`,
          location: `title-${lastSection}`,
        });
      }
      titles.forEach(
        (title, index) => (title.hidden = index !== currentSection)
      );
      lastSection = currentSection;
    }
  });

  window.addEventListener("scroll", function () {
    const currentSectionVertical = mainSections.findLastIndex(
      section =>
        section.offsetTop < window.scrollY + Math.floor(window.innerHeight / 2)
    );
    if (lastSection !== currentSectionVertical) {
      if (lastSection !== undefined) {
        logger.event({
          event_type: "scroll-vertical",
          target: `title-${currentSectionVertical}`,
          location: `title-${lastSection}`,
        });
      }

      lastSection = currentSectionVertical;
    }
  });

  window.addEventListener("wheel", function (event) {
    if (!breakpoint.matches) {
      return;
    }

    if (!event.target.closest(".page-content")) {
      return;
    }

    if (event.deltaX !== 0) {
      return;
    }

    pageContent.classList.add("scrolling");
    pageContent.scrollLeft += event.deltaY;
    setTimeout(() => pageContent.classList.remove("scrolling"));
  });

  document.addEventListener("click", function (event) {
    const button = event.target.closest(".page-nav__button");
    if (!button) {
      return;
    }

    const index = sections.findLastIndex(
      section =>
        section.offsetLeft <
        pageContent.scrollLeft + Math.floor(pageContent.offsetWidth / 2)
    );

    const newIndex = Math.max(
      0,
      Math.min(
        sections.length - 1,
        index + (button.classList.contains("prev") ? -1 : 1)
      )
    );

    pageContent.scrollLeft =
      sections[newIndex].offsetLeft -
      Math.max(
        0,
        (pageContent.offsetWidth - sections[newIndex].offsetWidth) / 2
      );

    logger.event({
      event_type: "scroll-button",
      target: `section-index-${newIndex}`,
      location: `section-index-${index}`,
    });
  });

  if (location.hash.length > 1) {
    const target = document.querySelector(location.hash);
    if (target.closest(".section")) {
      pageContent.scrollLeft = target.closest(".section").offsetLeft;
    }
  }
});
